import { AlertTriangle, Plus, X } from "lucide-react";
import SelectBuscable from "../../components/SelectBuscable";

/* Fix EV (2026-10-02): "Si tenemos un pedido de dos rollos de cable ... una marca un rollo y
   otra marca otro rollo, que te permita hacer la vinculación múltiple ... y en el caso de que
   el stock tenga una cantidad menor a lo que se pide, que el sistema reconozca eso y solamente
   adjudique lo que se tiene disponible, y no se elimine la línea" (CEN).
   Celda de origen de una línea: proveedor o stock interno, con uno o varios materiales (o
   herramientas) y la cantidad que sale de cada uno. Al elegir un material se propone lo que
   queda de la línea, hasta lo disponible en el Almacén Central (físico − reservado, lo mismo
   que reserva la aprobación en Inventario). Lo que no sale del stock sigue a proveedor. */

const r4 = (n) => Math.round((Number(n) || 0) * 10000) / 10000;

export function disponibleCentral(m) {
  const loc = (m?.locations || []).find(l => l.location_id === "almacen_central") || {};
  return Math.max(0, r4((Number(loc.quantity) || 0) - (Number(loc.reserved) || 0)));
}

export const asignadoTotal = (asig) => r4((asig || []).reduce((s, a) => s + (Number(a.cantidad) || 0), 0));

// Una celda está lista si cada material elegido tiene cantidad (hasta lo disponible) y entre
// todos no pasan la cantidad de la línea.
export function asignacionValida(fila, total, materials) {
  if (!fila || fila.origen === "proveedor") return true;
  const asig = fila.asign || [];
  if (!asig.length || asignadoTotal(asig) > r4(total) + 0.0001) return false;
  return asig.every(a => {
    if (!a.ref || !(Number(a.cantidad) > 0)) return false;
    if (fila.origen === "stock_herramientas") return true;
    const m = materials.find(x => x.id === a.ref.id);
    return !m || Number(a.cantidad) <= disponibleCentral(m) + 0.0001;
  });
}

export function CeldaAsignacionStock({ fila, total, unidad, onChange, materials, tools, testId, cerrado }) {
  const origen = fila?.origen || "proveedor";
  const asig = fila?.asign || [];
  const esHerr = origen === "stock_herramientas";
  const usado = asignadoTotal(asig);
  const resto = r4(total - usado);
  const opciones = esHerr
    ? tools.filter(t => t.status === "disponible").map(t => ({ value: t.id, label: `${t.code} · ${t.name}` }))
    : materials.map(m => ({ value: m.id, label: `${m.code} · ${m.name}${m.marca ? ` · ${m.marca}` : ""} — ${disponibleCentral(m)} ${m.unit || ""} disp.` }));

  const setOrigen = (o) => onChange({ origen: o, asign: o === "proveedor" ? [] : [{ ref: null, cantidad: r4(total) }] });
  const setAsign = (nuevas) => onChange({ origen, asign: nuevas });
  const elegir = (k, id) => {
    const ref = (esHerr ? tools : materials).find(x => x.id === id);
    if (!ref) return;
    const libre = r4(total - (usado - (Number(asig[k]?.cantidad) || 0)));
    const cantidad = esHerr ? libre : Math.min(libre, disponibleCentral(ref));
    setAsign(asig.map((a, j) => (j === k ? { ref: { id: ref.id, code: ref.code, name: ref.name }, cantidad } : a)));
  };

  if (cerrado) return <span className="text-[10px] text-gray-400">Adjudicado a un proveedor cerrado</span>;
  return (
    <div>
      <select value={origen} onChange={e => setOrigen(e.target.value)}
        className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs bg-white" data-testid={`${testId}-origen`}>
        <option value="proveedor">Comprar a proveedor</option>
        <option value="stock_materiales">Stock interno — Materiales</option>
        <option value="stock_herramientas">Stock interno — Herramientas</option>
      </select>
      {origen !== "proveedor" && (
        <div className="mt-1.5 space-y-1.5">
          {asig.map((a, k) => {
            const mat = !esHerr && a.ref ? materials.find(m => m.id === a.ref.id) : null;
            const disp = mat ? disponibleCentral(mat) : null;
            const excede = mat && Number(a.cantidad) > disp + 0.0001;
            const mal = !(Number(a.cantidad) > 0) || excede || asignadoTotal(asig) > r4(total) + 0.0001;
            return (
              <div key={k}>
                <div className="flex items-center gap-1.5">
                  <SelectBuscable value={a.ref?.id || ""} onChange={id => elegir(k, id)} options={opciones}
                    vacio={esHerr ? "Elegir herramienta..." : "Elegir material..."} etiquetaActual={a.ref ? `${a.ref.code} · ${a.ref.name}` : ""}
                    placeholderBuscar="Buscar por código o nombre..."
                    className="flex-1 min-w-0 border border-gray-300 rounded-lg px-2 py-1 text-[11px] bg-white" testId={`${testId}-ref-${k}`} />
                  <input type="number" min="0" step="any" value={a.cantidad}
                    onChange={e => setAsign(asig.map((x, j) => (j === k ? { ...x, cantidad: Number(e.target.value) || 0 } : x)))}
                    className={`w-16 border rounded-lg px-1.5 py-1 text-[11px] text-center ${mal ? "border-red-400 bg-red-50" : "border-gray-300"}`}
                    data-testid={`${testId}-cant-${k}`} />
                  {asig.length > 1 && (
                    <button type="button" onClick={() => setAsign(asig.filter((_, j) => j !== k))}
                      className="text-gray-400 hover:text-red-500" title="Quitar" data-testid={`${testId}-quitar-${k}`}><X size={12} /></button>
                  )}
                </div>
                {mat && (
                  <p className={`text-[10px] mt-0.5 flex items-center gap-1 ${excede ? "text-red-600 font-medium" : "text-gray-400"}`} data-testid={`${testId}-disp-${k}`}>
                    {excede && <AlertTriangle size={10} />} Disponible: {disp} {mat.unit || unidad}
                  </p>
                )}
                {esHerr && a.ref && Number(a.cantidad) > 1 && (
                  <p className="text-[10px] text-red-600 font-medium mt-0.5 flex items-center gap-1"><AlertTriangle size={10} /> La herramienta es unitaria (1)</p>
                )}
              </div>
            );
          })}
          <div className="flex items-center justify-between gap-2 text-[10px]">
            {resto > 0.0001 ? (
              <button type="button" onClick={() => setAsign([...asig, { ref: null, cantidad: resto }])}
                className="flex items-center gap-0.5 text-[#2E6DA4] font-semibold" data-testid={`${testId}-otro`}>
                <Plus size={10} /> {esHerr ? "Otra herramienta" : "Otro material"}
              </button>
            ) : <span />}
            <span className={resto > 0.0001 ? "text-amber-700 font-semibold" : "text-emerald-700 font-semibold"} data-testid={`${testId}-resto`}>
              {resto > 0.0001 ? `Queda para comprar: ${resto} ${unidad || ""}` : "Todo sale del stock"}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
